// LeadFilter.tsx - Main container component with state logic
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DatePicker } from "antd";
import moment from "moment";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import { getAllTeams } from "../../services/teamsService";

import { setProductsCache, setProductsLoading, resetLeadFilter } from "../../actions/actions";
import "./LeadFilterPopup.css";
import { CUSTOM_ACTIVITY_TYPE } from "../../utils/constants";
import logger, { Logger } from '../../utils/logger';
import PopupComponent from "../../components/PopupComponent";
import Tooltip from "../../components/Tooltip";
import {
  ChevronDown,
  ChevronRight,
  Search,
  X,
  Calendar,
  Users,
  Tag,
  Globe,
  Phone,
  Clock,
  Activity,
  Layers,
  Settings,
  RefreshCw,
   Package
} from "lucide-react";

// Import extracted sections and utilities
import {
  filterCategories,
  sortLeadBy,
  dateLeadAdded,
  operatorOptions,
  getDateRange,
  
  convertDateRangeToLabel,
} from "./LeadFilterUtils";

import {
  BasicFiltersSection,
  TeamFiltersSection,
  ActivityFiltersSection,
  TaskFiltersSection,
  SectionProps,
   ProductFiltersSection,
} from "./LeadFilterSections";
import { getAllProducts, Product } from "../../services/productService";

// Keep the original interface unchanged
interface PropsT {
  isOpen?: boolean;
  onClose?: () => void;
  onLabelClick: () => void;
  onStatusClick: () => void;
  selectedStatus: string;
  selectedLabel: Array<string>;
  setShowFilterDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  LeadFilterDataList: (objData: any) => void;
  initialActiveSection?: string; // New prop

}

interface SourceItem {
  value: string;
  name: string;
  type: 'hardcoded_source' | 'custom_source' | 'user_integration';
  isActive: boolean;
  integrationId: string;
}

interface CustomSource {
  value?: string;
  _id?: string;
  name: string;
  isActive?: boolean;
  integrationId?: string;
}

interface UserIntegration {
  integration: {
    _id: string;
    name: string;
  };
  isActive: boolean;
}

// ============================================
// 📚 TOOLTIP DEFINITIONS FOR ALL FILTERS
// ============================================
const filterTooltips = {
  // Basic Filters
  sortLeadBy: "Sort your leads by various criteria like date added, name, or last activity.",
  dateLead: "Filter leads based on when they were added to your system. Choose from preset ranges or select a custom date range.",
  label: "Filter leads by their assigned labels/tags. Multiple labels can be selected to narrow down results.",
  status: "Filter leads by their current status (New, Contacted, Qualified, etc.). Helps you focus on specific stages of your sales pipeline.",
  sources: "Filter by where the lead originated from (CSV upload, Lead Form, Google Sheets, or connected integrations).",

  // Team Filters
  team: "Filter leads by team assignment. Select specific teams or view organization-wide leads. Note: Selecting 'Organization' will show all leads across all teams regardless of assignment.",
  employee: "Filter leads owned by specific team members. This shows leads currently managed by each user. Useful for tracking individual workload and performance. Select multiple users to see all their combined leads.",
  assignedLead: "Show only leads that have been formally assigned to team members for follow-up. This helps identify leads that have clear ownership versus unassigned leads that need attention.",

  // Activity Filters
  customActivityName: "Filter by activity types like calls (incoming, outgoing, missed) or custom activities you've defined.",
  activitySelectedDate: "Filter leads based on when activities (calls, meetings, etc.) were logged. Choose from preset ranges or custom dates.",
  durationOperator: "Filter leads by call duration. Use operators like 'greater than', 'less than', or 'equal to' with a duration in seconds.",
  callCountOperator: "Filter leads by the number of calls made. Use operators to find leads with specific call counts.",

  // Task Filters
  taskType: "Filter leads by the types of tasks associated with them (Follow-up, Meeting, Call, etc.).",
  taskSelectedDate: "Filter leads based on when tasks were created or are due. Helps you prioritize upcoming or overdue tasks.",

  // Custom Field Filters
  selectCustomLead: "Filter by custom fields you've created in your lead form. Select a field to filter by its values.",
  selectCustomLeadSubField: "For group fields, select the specific sub-field you want to filter by.",
  operator: "Choose how to compare the field value (is, contains, greater than, less than, etc.).",
  leadformValue: "Enter the value to filter by. The type of input depends on the field type selected.",
  endLeadformValue: "For 'between' operators, enter the end value of the range.",

  // Product Filters
  products: "Filter leads that have been associated with specific products from your catalog."
};

// Tooltip content for filter sections
const sectionTooltips = {
  basic: "Basic filters help you sort and filter leads by common criteria like date added, labels, status, and source. Use these to narrow down your lead list quickly.",
  team: "Team filters let you view leads by team assignment, individual team members, or filter by assignment status. See who owns which leads and track team performance.",
  activity: "Activity filters allow you to find leads based on call history, activity types, and engagement patterns. Find leads with specific call durations, call counts, or activity dates.",
  task: "Task filters help you find leads with specific task types or task schedules, perfect for follow-up management. Track leads with upcoming or overdue tasks.",
  products: "Product filters let you find leads interested in or associated with specific products from your catalog. Track product interest and purchasing intent."
};

// Main component - kept as close to original as possible
const LeadFilter: React.FC<PropsT> = (props) => {
  const {
    isOpen = false,
    onClose = () => {},
    onLabelClick,
    onStatusClick,
    selectedLabel,
    setShowFilterDrawer,
    LeadFilterDataList,
    initialActiveSection,
  } = props;

  const dispatch = useDispatch();
  const { filterParam } = useSelector((state: any) => state.rootReducers.leads);

  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setLocalProductsLoading] = useState(false);

  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });
  



  // Keep all state variables from the original component
  const taskOptions =
    StoreData?.userData?.userDetails?.userPreference?.taskType?.map(
      (e: any) => ({
        ...e,
        label: e?.name,
      })
    );

  // ============================================
  // 🆕 ENHANCED: Include group fields with subfields structure
  // ============================================
  const customLeadOptions = React.useMemo(() => {
    const customForm = StoreData?.userData?.userDetails?.userPreference?.customForm;
    logger.log('🔍 Building customLeadOptions, customForm:', customForm);

    if (!customForm) {
      logger.log('⚠️ No customForm found');
      return undefined;
    }

    const options: any[] = [];

    customForm.forEach((field: any) => {
      logger.log(`🔍 Processing field: ${field.name}, type: ${field.type}, value: ${field.value}`);

      // Skip address fields (existing logic)
      if (field.value?.includes("address")) {
        logger.log(`⏭️ Skipping address field: ${field.value}`);
        return;
      }

      // If it's a group field, keep it as a group (don't flatten)
      if (field.type === "group" && field.subFields) {
        logger.log(`📦 Adding group field for filter: ${field.name} with ${field.subFields.length} subfields`);

        const groupOption = {
          ...field,
          label: field.name,
          value: field.value,
          isGroup: true,
          subFields: field.subFields // Keep subFields for nested selection
        };

        options.push(groupOption);
        logger.log(`✅ Added group field:`, groupOption);
      } else {
        // Regular field
        const regularOption = {
          ...field,
          label: field?.name,
        };
        options.push(regularOption);
        logger.log(`✅ Added regular field:`, regularOption);
      }
    });

    logger.log(`🎯 Total filterable custom fields: ${options.length}`, options);
    return options;
  }, [StoreData?.userData?.userDetails?.userPreference?.customForm]);

  // Combine predefined call types with user-defined activity types
  const userActivityTypes = StoreData?.userData?.userDetails?.userPreference?.activityType || [];
  const activityTypes = [
    // Add predefined call types first
    ...CUSTOM_ACTIVITY_TYPE.filter(type => 
      ['INCOMING', 'OUTGOING', 'MISSED'].includes(type.value)
    ),
    // Then add user-defined activity types, avoiding duplicates
    ...userActivityTypes.filter((userType: any) => 
      !['INCOMING', 'OUTGOING', 'MISSED'].includes(userType.value)
    )
  ];

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState("");

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>(() => {
    // Initialize with default open sections
    const initialState: {[key: string]: boolean} = {};
    filterCategories.forEach(category => {
      initialState[category.id] = category.defaultOpen;
    });
    return initialState;
  });

  const [teamMembers, setTeamMembers] = useState<Array<any>>([]);
  const [startDate, setStartDate] = useState<any>(undefined);
  const [endDate, setEndDate] = useState<any>(undefined);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [multiSelections, setMultiSelections] = useState([]);
  const [isAscending, setIsAscending] = useState<boolean>(
    filterParam?.sort?.isAscending ?? false
  );
  const [orderBy, setOrderBy] = useState<any>([]);
  const [labels, setLabels] = useState<any>([]);
  const [status, setStatus] = useState<any>([]);
  const [leadSource, setLeadSource] = useState<any>([]);
  const [employee, setEmployee] = useState<any>([]);
  const [filterData, setFilterData] = useState<any>({});
  const [isOrganization, setIsOrganization] = useState<boolean>(false);
  const [isOpenAdvanced, setIsOpenAdvanced] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState("");
  const [taskFromDate, setTaskFromDate] = useState<any>(undefined);
  const [taskToDate, setTaskToDate] = useState<any>(undefined);
  const [activityFromDate, setActivityFromDate] = useState<any>(undefined);
  const [activityToDate, setActivityToDate] = useState<any>(undefined);

  const [callDurationValue, setCallDurationValue] = useState<string>("");
  const [callCountValue, setCallCountValue] = useState<string>("");
  const [durationOperator, setDurationOperator] = useState<any>(undefined);
  const [callCountOperator, setCallCountOperator] = useState<any>(undefined);
  
  // Add useRef for render count tracking
  const renderCountRef = useRef<number>(0);

  logger.log("Filter Data State:", filterData);


useEffect(() => {

  
  try {
    // 1. Get data from localStorage
    const userPreferences = JSON.parse(localStorage.getItem("user_preferences") || "{}");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    
    logger.log('📦 Raw data check:', {
      hasUserPreferences: !!userPreferences,
      hasUserData: !!userData,
      hasCustomSource: !!userPreferences?.customSource,
      hasUserIntegrations: !!userData?.userIntegrations,
      customSourceCount: userPreferences?.customSource?.length || 0,
      userIntegrationsCount: userData?.userIntegrations?.length || 0
    });
    
    // 2. Get custom sources from userPreferences
    const customSources: CustomSource[] = userPreferences?.customSource || [];
    logger.log('📝 Custom sources:', customSources.length, customSources);
    
    // 3. Get user integrations from userData
    const userIntegrations: UserIntegration[] = userData?.userIntegrations || [];
    logger.log('🔗 User integrations:', userIntegrations.length, userIntegrations);
    
    // 4. Combine all sources for leadSource state
    const combinedSources: SourceItem[] = [];
    
    // ============================================
    // 🔥 STEP 1: Add HARDCODED sources first (always available)
    // Real integration IDs from your system
    // ============================================
    const hardcodedSources: SourceItem[] = [
      {
        value: 'CSV',
        name: 'CSV',
        type: 'hardcoded_source',
        isActive: true,
        integrationId: '64109561e6c7e737227a637c' // Real CSV integration ID
      },
      {
        value: 'Lead Form',
        name: 'Lead Form',
        type: 'hardcoded_source', 
        isActive: true,
        integrationId: '64109561e6c7e737227a6382' // Real Lead Form integration ID
      },
      {
        value: 'Google spread sheets',
        name: 'Google spread sheets',
        type: 'hardcoded_source',
        isActive: true,
        integrationId: '65ccd9a61a0a18534633c277' // Real Google Sheets integration ID
      }
    ];
    
    combinedSources.push(...hardcodedSources);
    logger.log('✅ Added hardcoded sources:', hardcodedSources.length, hardcodedSources.map((s: SourceItem) => s.name));
    
    // ============================================
    // 🔥 STEP 2: Add custom sources from userPreferences
    // ============================================
    if (customSources.length > 0) {
      const formattedCustomSources: SourceItem[] = customSources
        .filter((source: CustomSource) => source && (source.value || source._id) && source.name)
        .map((source: CustomSource) => ({
          value: source.value || source._id || '',
          name: source.name,
          type: 'custom_source' as const,
          isActive: source.isActive !== false,
          integrationId: source.integrationId || source.value || source._id || ''
        }));
      
      combinedSources.push(...formattedCustomSources);
      logger.log('✅ Added custom sources:', formattedCustomSources.length, formattedCustomSources.map((s: SourceItem) => s.name));
    }
    
    // ============================================
    // 🔥 STEP 3: Add user integrations (only active ones)
    // ============================================
    if (userIntegrations.length > 0) {
      const formattedIntegrations: SourceItem[] = userIntegrations
        .filter((userIntegration: UserIntegration) => 
          userIntegration && 
          userIntegration.integration && 
          userIntegration.integration._id && 
          userIntegration.integration.name &&
          userIntegration.isActive === true
        )
        .map((userIntegration: UserIntegration) => ({
          value: userIntegration.integration._id,
          name: userIntegration.integration.name,
          type: 'user_integration' as const,
          isActive: true,
          integrationId: userIntegration.integration._id
        }));
      
      combinedSources.push(...formattedIntegrations);
      logger.log('✅ Added user integrations:', formattedIntegrations.length, formattedIntegrations.map((s: SourceItem) => s.name));
    }
    
    // ============================================
    // 🔥 STEP 4: Remove duplicates (if any) and set final state
    // ============================================
    const uniqueSources: SourceItem[] = [];
    const seenValues = new Set<string>();
    
    combinedSources.forEach((source: SourceItem) => {
      if (!seenValues.has(source.value)) {
        seenValues.add(source.value);
        uniqueSources.push(source);
      } else {
        logger.log('⚠️ Duplicate source filtered out:', source.name);
      }
    });
    
    // 5. Set leadSource state with combined data
    setLeadSource(uniqueSources);
    logger.log('🎯 Final leadSource set:', {
      totalSources: uniqueSources.length,
      hardcodedCount: uniqueSources.filter((s: SourceItem) => s.type === 'hardcoded_source').length,
      customCount: uniqueSources.filter((s: SourceItem) => s.type === 'custom_source').length,
      integrationCount: uniqueSources.filter((s: SourceItem) => s.type === 'user_integration').length,
      sources: uniqueSources.map((s: SourceItem) => ({ name: s.name, type: s.type, integrationId: s.integrationId }))
    });
    
    // 6. Set other filter data (unchanged)
    setLabels(userPreferences?.labels || []);
    setStatus(userPreferences?.status || []);

    // 7. 🔒 SECURITY FIX: Set employee data with role-based filtering (same as dashboard)
    const userRole = userData?.role?.name;
    const subordinateData = userData?.subordinateEmployees;

    // 📊 COMPREHENSIVE ROLE LOGGING
    logger.log('🎭 leadFilter Role Check:', {
      userRole: userRole,
      hasSubordinateData: !!subordinateData,
      subordinateDataKeys: subordinateData ? Object.keys(subordinateData) : []
    });

    let filteredEmployees: any[] = [];

    if (userRole === 'super_admin') {
      // Super admin gets all organization employees
      const orgEmployees = userData?.organizationEmployee || [];
      const reportTo = subordinateData?.reportTo || [];
      const teamMembers = subordinateData?.teamMembers || [];
      const orgMembers = subordinateData?.organizationMember || [];

      logger.log('👑 Super admin employee data:', {
        orgEmployeesCount: orgEmployees.length,
        reportToCount: reportTo.length,
        teamMembersCount: teamMembers.length,
        orgMembersCount: orgMembers.length
      });

      // ✅ FIX: If orgEmployees is empty but subordinateData exists, use subordinateData
      if (orgEmployees.length === 0 && (reportTo.length > 0 || teamMembers.length > 0 || orgMembers.length > 0)) {
        const allSubordinates = [...reportTo, ...teamMembers, ...orgMembers];
        // Remove duplicates
        const uniqueEmployees = allSubordinates.filter((user, index, array) =>
          array.findIndex(u => u._id === user._id) === index
        );

        logger.log('👑 Super admin using subordinate data:', {
          source: 'subordinateEmployees (fallback)',
          count: uniqueEmployees.length
        });

        filteredEmployees = uniqueEmployees;
      } else {
        logger.log('👑 Super admin using employees:', {
          source: 'organizationEmployee',
          count: orgEmployees.length
        });

        filteredEmployees = orgEmployees;
      }
    } else {
      // Get subordinate arrays
      const reportTo = subordinateData?.reportTo || [];
      const teamMembers = subordinateData?.teamMembers || [];
      const orgMembers = subordinateData?.organizationMember || [];

      logger.log('📊 Subordinate data breakdown:', {
        reportToCount: reportTo.length,
        teamMembersCount: teamMembers.length,
        orgMembersCount: orgMembers.length
      });

      if (userRole === 'employee') {
        // 🔒 EMPLOYEES: Can ONLY see themselves
        logger.log('🔒 Employee: Restricting to self only');
        const currentUser = {
          firstName: userData?.firstName || '',
          lastName: userData?.lastName || '',
          _id: userData?._id,
          email: userData?.email || '',
        };
        filteredEmployees = [currentUser];
      } else if (userRole === 'manager' || userRole === 'team_lead') {
        // 🔓 MANAGERS/TEAM LEADS: Can see their subordinates
        logger.log('🔓 Manager/Team Lead: Showing subordinates');
        const allSubordinates = [...reportTo, ...teamMembers];
        // Remove duplicates
        filteredEmployees = allSubordinates.filter((user, index, array) =>
          array.findIndex(u => u._id === user._id) === index
        );
      } else {
        // 🔓 OTHER ROLES: Can see organization members
        logger.log('🔓 Other role: Showing org members');
        const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
        // Remove duplicates
        filteredEmployees = allUsers.filter((user, index, array) =>
          array.findIndex(u => u._id === user._id) === index
        );
      }
    }

    logger.log('✅ leadFilter: Filtered employees', {
      role: userRole,
      count: filteredEmployees.length,
      source: userRole === 'super_admin' ? 'organizationEmployee or fallback' : 'subordinateEmployees'
    });

    setEmployee(filteredEmployees);

    logger.log('✅ ALL FILTER DATA SET:', {
      leadSourceCount: uniqueSources.length,
      labelsCount: (userPreferences?.labels || []).length,
      statusCount: (userPreferences?.status || []).length,
      employeeCount: filteredEmployees.length
    });
    
  } catch (error) {
    logger.error('❌ Error in sources loading:', error);
    
    // Fallback: at least show hardcoded sources with real integration IDs
    const fallbackSources: SourceItem[] = [
      {
        value: 'CSV',
        name: 'CSV',
        type: 'hardcoded_source',
        isActive: true,
        integrationId: '64109561e6c7e737227a637c'
      },
      {
        value: 'Lead Form',
        name: 'Lead Form', 
        type: 'hardcoded_source',
        isActive: true,
        integrationId: '64109561e6c7e737227a6382'
      },
      {
        value: 'Google spread sheets',
        name: 'Google spread sheets',
        type: 'hardcoded_source',
        isActive: true,
        integrationId: '65ccd9a61a0a18534633c277'
      }
    ];
    
    setLeadSource(fallbackSources);
    setLabels([]);
    setStatus([]);
    setEmployee([]);
  }
  
  
}, []); // Run once on mount

  useEffect(() => {
  logger.log('🔄 FILTER LIFECYCLE - MOUNTED:', new Date().toISOString());
  return () => {
    logger.log('🔄 FILTER LIFECYCLE - UNMOUNTED:', new Date().toISOString());
  };
}, []);



useEffect(() => {
  const fetchProducts = async () => {
    try {
      setLocalProductsLoading(true);
      // Also dispatch Redux loading state
      dispatch(setProductsLoading(true));
    
      const response = await getAllProducts({
        page: 1,
        perPage: 500
      });
      
      if (response?.data?.data) {
        setProducts(response.data.data); // Keep local state for this component
        
        // ADD THIS: Store in Redux for other components to access
        dispatch(setProductsCache(response.data.data));
      }
    } catch (error) {
    } finally {
      setLocalProductsLoading(false);
      // Also dispatch Redux loading state
      dispatch(setProductsLoading(false));
    }
  };

  fetchProducts();
}, [dispatch]); // Add dispatch to dependencies




useEffect(() => {
  
  // First set state based on byOrganization flag if present
  if (filterParam.byOrganization) {
    setIsOrganization(true);
  }
  
  // Find organization option in teamMembers array for correct structure
  const organizationOption = teamMembers.find(member => member._id === "organization");
  
  setFilterData({
  ...filterData,
  sortLeadBy: sortLeadBy.find((s) => s.value === filterParam.sort?.orderBy),
  dateLead:
      filterParam.date?.startedAt && filterParam.date?.endedAt
        ? convertDateRangeToLabel(
            filterParam.date.startedAt,
            filterParam.date.endedAt
          ) || { value: "custom", label: "Custom Date" } // Default to custom if no matching preset
        : undefined,
        
    // For custom dates, also set start and end dates in component state
    // This is what's missing in your current implementation
    ...(filterParam.date?.startedAt && filterParam.date?.endedAt && 
       convertDateRangeToLabel(filterParam.date.startedAt, filterParam.date.endedAt)?.value === "custom" 
       ? {
           startDate: moment(filterParam.date.startedAt),
           endDate: moment(filterParam.date.endedAt),
           showDatePicker: true
         } 
       : {}),
  label: filterParam?.label
    ? filterParam.label.map((item: string) => {
        const index = labels.findIndex((l: any) => l.value === item);
        return {
          value: item,
          label: index !== -1 ? labels[index].name : "",
        };
      })
    : [],
  status: filterParam?.status
    ? filterParam.status.map((item: string) => {
        const index = status.findIndex(
          (statusItem: any) => statusItem.value === item
        );
        return {
          value: item,
          label: index !== -1 ? status[index].name : "",
        };
      })
    : [],
    sources: filterParam?.source
    ? filterParam.source.map((item: string) => {
       
        
        // Use systemIntegration from StoreData to find the matching name
        const integration = StoreData?.userData?.userDetails?.systemIntegration?.find(
          (int: any) => int._id === item
        );
        
        const result = {
          value: item,
          label: integration?.name || item
        };
        
       
        
        return result;
      })
    : [],
    team: filterParam.byOrganization && organizationOption
    ? [{ value: organizationOption._id, label: organizationOption.showName }]
    : filterParam?.teams
      ? filterParam.teams.map((item: string) => {
          const index = teamMembers.findIndex(
            (teamMember: any) => teamMember._id === item
          );
          return {
            value: item,
            label: index !== -1 ? teamMembers[index].showName : "",
          };
        })
      : [],
  employee: filterParam?.teamMembers
    ? filterParam.teamMembers.map((item: string) => {
        const index = employee.findIndex((emp: any) => emp._id === item);
        return {
          value: item,
          label:
            index !== -1
              ? employee[index].firstName + " " + employee[index].lastName
              : "",
        };
      })
    : [],
  // Parse group subfield if fieldName contains a dot (e.g., "accessory.type")
  selectCustomLead: (() => {
    const fieldName = filterParam?.customFieldFilter?.fieldName;
    if (!fieldName) return undefined;

    // Check if it's a nested group field
    if (fieldName.includes('.')) {
      const [groupValue] = fieldName.split('.');
      return customLeadOptions?.find((e: any) => e.value === groupValue && e.isGroup);
    }

    // Regular field
    return customLeadOptions?.find((e: any) => e.value === fieldName);
  })(),

  // Parse subfield selection for group fields
  selectCustomLeadSubField: (() => {
    const fieldName = filterParam?.customFieldFilter?.fieldName;
    if (!fieldName || !fieldName.includes('.')) return undefined;

    const [groupValue, subFieldValue] = fieldName.split('.');
    const groupField = customLeadOptions?.find((e: any) => e.value === groupValue && e.isGroup);

    if (groupField?.subFields) {
      const subField = groupField.subFields.find((sf: any) => sf.value === subFieldValue);
      if (subField) {
        return {
          ...subField,
          label: subField.name,
          value: fieldName,  // Full nested path
          actualValue: subFieldValue,
          parentGroup: groupValue
        };
      }
    }

    return undefined;
  })(),
  leadformValue: filterParam?.customFieldFilter?.range?.start?.value || filterParam?.customFieldFilter?.single?.value,
  endLeadformValue: filterParam?.customFieldFilter?.range?.end?.value,
  operator: filterParam?.customFieldFilter?.range 
    ? { label: "between", value: "between" }
    : filterParam?.customFieldFilter?.single?.operator 
      ? operatorOptions.find(op => op.value === filterParam?.customFieldFilter?.single?.operator)
      : undefined,
  taskType: taskOptions?.filter((e: any) =>
    filterParam?.taskFilter?.types?.includes(e.value)
  ),
  taskSelectedDate:
    filterParam?.taskFilter?.range?.start &&
    filterParam?.taskFilter?.range?.end
      ? convertDateRangeToLabel(
          filterParam?.taskFilter?.range?.start,
          filterParam?.taskFilter?.range?.end
        )
      : undefined,
  customActivityName: activityTypes
    ?.filter((e: any) =>
      filterParam?.activityFilter?.types?.includes(e?.value)
    )
    ?.map((e: any) => ({ ...e, label: e.name })),
  activitySelectedDate:
    filterParam?.activityFilter?.range?.start &&
    filterParam?.activityFilter?.range?.end
      ? convertDateRangeToLabel(
          filterParam?.activityFilter.range.start,
          filterParam?.activityFilter.range.end
        )
      : undefined,

      callDurationValue: filterParam?.activityFilter?.duration?.toString() || "",
      callCountValue: filterParam?.activityFilter?.callCount?.toString() || "",
      durationOperator: operatorOptions.find(
        op => op.value === filterParam?.activityFilter?.durationOperator
      ),
    callCountOperator: operatorOptions.find(
        op => op.value === filterParam?.activityFilter?.callCountOperator
      ),
      
      products: filterParam?.products?.map((productId: string) => {
        // Try to get stored product name first
        const storedName = filterParam._productNames?.[productId];
        if (storedName) {
          return { value: productId, label: `${storedName} - ₹0` }; // Placeholder price
        }
        
        // Try to find in loaded products
        const foundProduct = products.find(p => p._id === productId);
        if (foundProduct) {
          return { 
            value: foundProduct._id, 
            label: `${foundProduct.name} - ₹${foundProduct.price.toLocaleString()}` 
          };
        }
        
        // Fallback
        return { value: productId, label: `Product ${productId.slice(-6)}` };
      }) || [],

  assignedLead: employee
    ?.filter((e: any) => filterParam?.teamMembers?.includes(e?._id))
    ?.map((v: any) => {
      return {
        value: v?._id,
        label:
          v?.firstName && v?.lastName
            ? v?.firstName + " " + v?.lastName
            : v?.firstName,
      };
    }),
});

}, [labels, leadSource, status, teamMembers, employee, filterParam]);

// Replace the existing fetchTeams function in your LeadFilter.tsx with this safe version:

const fetchTeams = async () => {
 
  
  try {
    // Check user role before making API call
    const userRole = StoreData?.userData?.userDetails?.role?.name;
    logger.log('🔒 User role check:', userRole);
    
    // Only call teams API if user is NOT an employee
    if (userRole !== 'employee') {
      logger.log('✅ User is not employee, fetching teams data');
      const response = await getAllTeams();
      
      if (response && response.status) {
        let tempArray = [...(response.data?.data || [])];
        
        // Safely add "Team" suffix to each team name
        tempArray.forEach((el: any) => {
          if (el && el.name) {
            el.showName = el.name + "-" + "Team";
          }
        });

        // Safe null checking for organization data
        const organizationName = StoreData?.userData?.userDetails?.organization?.name;
        
        let tempOrgData;
        
        if (organizationName) {
          // Use actual organization name if available
          tempOrgData = {
            showName: organizationName + "-" + "Organization",
            _id: "organization",
          };
          logger.log('✅ Organization data found:', organizationName);
        } else {
          // Fallback organization entry
          tempOrgData = {
            showName: "Organization", // Generic fallback name
            _id: "organization",
          };
          logger.warn('⚠️ Organization name not found, using fallback. Available data:', {
            hasStoreData: !!StoreData,
            hasUserData: !!StoreData?.userData,
            hasUserDetails: !!StoreData?.userData?.userDetails,
            hasOrganization: !!StoreData?.userData?.userDetails?.organization
          });
        }
        
        tempArray.push(tempOrgData);
        setTeamMembers(tempArray);
        logger.log('✅ Teams data loaded:', tempArray.length);
        
      } else {
        logger.error('❌ Failed to fetch teams data - Invalid response');
        setTeamMembers([]);
      }
    } else {
      logger.log('🚫 User is employee, skipping teams API call');
      // For employees, only set empty teams array
      setTeamMembers([]);
    }
    
  } catch (error) {
    logger.error('❌ Error in fetchTeams function:', error);
    // Set empty array as fallback to prevent further crashes
    setTeamMembers([]);
  }
  
  
};

useEffect(() => {
fetchTeams();
}, []);

/**
 * Maps source display values to integration IDs for API calls
 */
const buildSourceFilter = (selectedSources: any[]): string[] => {
  if (!selectedSources?.length) return [];
  
  // Map display values to integration IDs
  const sourceIntegrationIds = selectedSources.map((source: any) => {
    // Handle both object format {value: "CSV"} and string format "CSV"
    const sourceValue = typeof source === 'string' ? source : source.value;
    
    // Map hardcoded sources to their integration IDs
    const hardcodedSourceMap: { [key: string]: string } = {
      'CSV': '64109561e6c7e737227a637c',
      'Lead Form': '64109561e6c7e737227a6382', 
      'Google spread sheets': '65ccd9a61a0a18534633c277'
    };
    
    // Return integration ID if hardcoded, otherwise use the value as-is
    return hardcodedSourceMap[sourceValue] || sourceValue;
  });
  
  logger.log('🔄 Source filter mapping:', {
    selectedSources: selectedSources.map((s: any) => typeof s === 'string' ? s : s.value),
    integrationIds: sourceIntegrationIds
  });
  
  return sourceIntegrationIds;
};

const handleFilter = (e: any, name: any) => {

  // Handle bulk updates (multiple fields at once)
  if (name === "bulk") {
    logger.log('🔄 Bulk filter update:', e);
    setFilterData({ ...filterData, ...e });
    return;
  }

  // Only add special handling for team selection
  if (name === "team") {
    // If user selects any team dropdown options
    if (e && e.length > 0) {
      // Check if user selected "Organization"
      const hasOrganization = e.some((item: any) => item.value === "organization");

      if (hasOrganization) {
        // If "Organization" is selected, set the flag and clear team selection to just organization
        setIsOrganization(true);
        // Only keep the organization option in the dropdown
        setFilterData({
          ...filterData,
          [name]: e.filter((item: any) => item.value === "organization")
        });
      } else {
        // If regular teams selected, ensure organization flag is off
        setIsOrganization(false);
        setFilterData({ ...filterData, [name]: e });
      }
    } else {
      // Nothing selected, reset both
      setIsOrganization(false);
      setFilterData({ ...filterData, [name]: [] });
    }
    return; // Exit early after handling team selection
  }

  // Regular handling for other filter types
  setFilterData({ ...filterData, [name]: e });
};

const dateFilterOnChange = (e: any, name: string) => {
logger.log(`[dateFilterOnChange] name: ${name}, value:`, e);
logger.log(`Date filter change: ${name}`, e);
setFilterData({ ...filterData, [name]: e });
switch (e?.value) {
  case "today":
    logger.log('[dateFilterOnChange] Setting startDate and endDate for TODAY');
    setStartDate(moment.utc().startOf('day').format());
    setEndDate(moment.utc().endOf('day').format());
    setShowDatePicker(false);
    break;
  case "yesterday":
    setStartDate(moment.utc().subtract(1, "d").startOf('day').format());
    setEndDate(moment.utc().subtract(1, "d").endOf('day').format());
    setShowDatePicker(false);
    break;
  case "3DayBefore":
    setStartDate(moment.utc().subtract(3, "d").startOf('day').format());
    setEndDate(moment.utc().endOf('day').format());
    setShowDatePicker(false);
    break;
  case "1WeekBefore":
    setStartDate(moment.utc().subtract(1, "w").startOf('day').format());
    setEndDate(moment.utc().endOf('day').format());
    setShowDatePicker(false);
    break;
  case "1MonthBefore":
    setStartDate(moment.utc().subtract(30, "d").startOf('day').format());
    setEndDate(moment.utc().endOf('day').format());
    setShowDatePicker(false);
    break;
  case "custom":
    setShowDatePicker(true);
    break;
  default:
    break;
}
};

const selectTeamMember = (val: any) => {
setMultiSelections(val);
};

const selectOrderBy = (e: React.ChangeEvent<HTMLSelectElement>) => {
setOrderBy(e?.currentTarget?.value);
};

const handleRadio = (e: React.ChangeEvent<HTMLInputElement>) => {
const isAsc = e?.currentTarget?.value === "true" ? true : false;
setIsAscending(isAsc);
};

// Toggle section expansion
const toggleSection = (sectionId: string) => {
  const isCurrentlyExpanded = expandedSections[sectionId];

  setExpandedSections(prev => ({
    ...prev,
    [sectionId]: !prev[sectionId]
  }));

  // If we're expanding a section (not collapsing), scroll to show it
  if (!isCurrentlyExpanded) {
    // Use setTimeout to wait for the DOM to update after state change
    setTimeout(() => {
      const targetElement = document.getElementById(`filter-section-${sectionId}`);
      if (targetElement) {
        // Get the scrollable container (filter-options-column)
        const scrollContainer = document.querySelector('.filter-options-column');
        if (scrollContainer) {
          // Calculate the target scroll position
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;

          // Calculate offset to scroll to the top of the target section
          const targetScrollTop = scrollTop + targetRect.top - containerRect.top - 20; // 20px padding

          scrollContainer.scrollTo({
            top: Math.max(0, targetScrollTop),
            behavior: 'smooth'
          });
        }
      }
    }, 100);
  }
};

// Helper method for operator options
const getOperatorOptions = () => {
  if (!filterData?.selectCustomLead) return [];

  // For group fields, check subfield type; for regular fields, check main field type
  let isExist;
  if (filterData?.selectCustomLeadSubField) {
    // Use the subfield object directly - it has the type property
    isExist = filterData.selectCustomLeadSubField;
  } else {
    // Regular field - find in customLeadOptions
    isExist = customLeadOptions?.find(
      (e: any) => e.value === filterData?.selectCustomLead?.value
    );
  }

  switch (isExist?.type) {
    case "name":
    case "email":
    case "text":
      return [
        { label: "is", value: "eq" },
        { label: "contain", value: "contain" },
        { label: "does not contain", value: "doesNotContain" },
        { label: "is not", value: "ne" },
      ];

    case "number":
    case "quantity":
      return [
        { label: "greater than", value: "gt" },
        { label: "less than", value: "lt" },
        { label: "is", value: "eq" },
        { label: "between", value: "between" },
      ];

    case "amount":
      return [
        { label: "greater than", value: "gt" },
        { label: "less than", value: "lt" },
        { label: "is", value: "eq" },
        { label: "is not", value: "ne" },
        { label: "between", value: "between" },
      ];

    case "date":
      return [
        { label: "is", value: "eq" },
        { label: "is before", value: "lt" },
        { label: "is after", value: "gt" },
        { label: "between", value: "between" },
      ];

    case "selection":
      return [
        { label: "is", value: "eq" },
        { label: "is not", value: "ne" },
      ];

    case "multi-selection":
      return [
        { label: "contains", value: "contain" },
        { label: "does not contain", value: "doesNotContain" },
      ];

    case "phone":
      return [
        { label: "contain", value: "contain" },
        { label: "does not contain", value: "doesNotContain" },
      ];

    default:
      return [];
  }
};

const isOptionsExist = () => {
  if (!filterData?.selectCustomLead) return [];

  let isExist;

  // For group fields, check subfield; for regular fields, check main field
  if (filterData?.selectCustomLeadSubField) {
    // Use the subfield object directly - it already has type and options
    isExist = filterData.selectCustomLeadSubField;
  } else {
    // Regular field - find in customForm
    let leadOptions =
      StoreData?.userData?.userDetails?.userPreference?.customForm;
    isExist = leadOptions?.find(
      (e: any) => e.value === filterData?.selectCustomLead?.value
    );
  }

  // Allow options for both selection and multi-selection
  if (isExist?.type !== "selection" && isExist?.type !== "multi-selection") return [];

  return isExist?.options?.map((e: string) => ({ label: e, value: e }));
};

const advanceDateFilterOnChange = (
e: any,
name: string,
callback: (e: any) => any
) => {
logger.log(`[advanceDateFilterOnChange] name: ${name}, value:`, e, 'callback:', callback.name);
setFilterData({ ...filterData, [name]: e });
switch (e?.value) {
  case "today":
    logger.log('[advanceDateFilterOnChange] Calling callback for TODAY');
    callback(moment.utc().startOf('day').format());
    setDatePickerKey("");
    break;
  case "yesterday":
    callback(moment.utc().subtract(1, "d").startOf('day').format());
    setDatePickerKey("");
    break;
  case "3DayBefore":
    callback(moment.utc().subtract(3, "d").startOf('day').format());
    setDatePickerKey("");
    break;
  case "1WeekBefore":
    callback(moment.utc().subtract(1, "w").startOf('day').format());
    setDatePickerKey("");
    break;
  case "1MonthBefore":
    callback(moment.utc().subtract(30, "d").startOf('day').format());
    setDatePickerKey("");
    break;
  case "custom":
    setDatePickerKey(name);
    break;
  default:
    break;
}
};

// Filter the categories based on search term
const filteredCategories = useMemo(() => {
if (!searchTerm) return filterCategories;


return filterCategories.map(category => {
  // Check if category name matches
  const categoryMatches = category.label.toLowerCase().includes(searchTerm.toLowerCase());
  
  // Check if any filter in this category matches
  const hasMatchingFilters = category.filters.some(filterId => {
    // Map filter IDs to human-readable names for searching
    const filterNameMap: {[key: string]: string} = {
      sortLeadBy: "Sort by",
      dateLead: "Date added",
      label: "Labels",
      status: "Status",
      sources: "Sources",
      team: "Team",
      employee: "Employee",
      assignedLead: "Assigned",
      durationOperator: "Call duration",
      callCountOperator: "Call count",
      customActivityName: "Activity",
      activitySelectedDate: "Activity date",
      taskType: "Task type",
      taskSelectedDate: "Task date",
      selectCustomLead: "Custom field",
      operator: "Operator",
      leadformValue: "Field value"
    };
    
    const filterName = filterNameMap[filterId] || filterId;
    return filterName.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  // If category or any filter matches, auto-expand this section
  if (categoryMatches || hasMatchingFilters) {
    if (hasMatchingFilters && !expandedSections[category.id]) {
      // Auto-expand the section if a filter within it matches
      setExpandedSections(prev => ({
        ...prev,
        [category.id]: true
      }));
    }
    return {
      ...category,
      matches: true
    };
  }
  
  return {
    ...category,
    matches: false
  };
}).filter(category => category.matches);
}, [searchTerm, expandedSections]);

const resetFilter = () => {

  
  // Explicitly reset all state variables to their initial values
  
  // 1. Reset main filter data with all fields explicitly defined
  setFilterData({
    sortLeadBy: undefined,
    dateLead: undefined,
    label: [],
    status: [],
    sources: [],
    team: [],
    employee: [],
    assignedLead: [],
    durationOperator: undefined,
    callCountOperator: undefined,
    callDurationValue: "",
    callCountValue: "",
    customActivityName: [],
    activitySelectedDate: undefined,
    taskType: [],
    taskSelectedDate: undefined,
    selectCustomLead: undefined,
    operator: undefined,
    leadformValue: "",
    endLeadformValue: "",
     products: [] 
  });
  
  // 2. Reset all UI state flags
  setIsAscending(false);
  setIsOrganization(false); // Reset organization flag
  
  // 3. Reset all date-related states
  setStartDate(undefined);
  setEndDate(undefined);
  setShowDatePicker(false);
  setTaskFromDate(undefined);
  setTaskToDate(undefined);
  setActivityFromDate(undefined);
  setActivityToDate(undefined);
  
  // 4. Reset all filter values to empty string (not undefined to avoid controlled/uncontrolled warning)
  setDurationOperator(undefined);
  setCallCountOperator(undefined);
  setCallDurationValue("");
  setCallCountValue("");
  
  // 5. Reset date picker keys
  setDatePickerKey("");
  
  
  // 6. Reset Redux filter state

  dispatch(resetLeadFilter());
  

};

const applyFilter = async () => {


// Same complex filter object construction
var tempLabel = filterData?.label;
var filterLabel = [];
for (let i = 0; i < tempLabel?.length; i++) {
  filterLabel.push(tempLabel[i].value);
}
var tempStatus = filterData?.status;
var filterStatus = [];
for (let i = 0; i < tempStatus?.length; i++) {
  filterStatus.push(tempStatus[i].value);
}
var tempProducts = filterData?.products;
var filterProducts = [];
var productNames: { [productId: string]: string } = {}; // Properly typed object
for (let i = 0; i < tempProducts?.length; i++) {
  filterProducts.push(tempProducts[i].value);
  // Store product name (remove price from label)
  productNames[tempProducts[i].value] = tempProducts[i].label.split(' - ₹')[0];
}


var tempSource = filterData?.sources;
var filterSource = [];
for (let i = 0; i < tempSource?.length; i++) {
  logger.log('Applying Source Filter:', {
    value: tempSource[i].value,
    label: tempSource[i].label
  });
  filterSource.push(tempSource[i].value);  // Always use the original ID
}

var tempEmp = filterData?.employee;
var filterEmp = [];
for (let i = 0; i < tempEmp?.length; i++) {
  filterEmp.push(tempEmp[i].value);
}

var tempTeam = filterData?.team;
var filterTeame = [];
if (filterData?.team) {
  if (tempTeam[0]?.value !== "organization") {
    for (let i = 0; i < tempTeam?.length; i++) {
      filterTeame.push(tempTeam[i].value);
    }
  }
}
logger.log('[applyFilter] Initial date values:', {
  startDate,
  endDate,
  taskFromDate,
  taskToDate,
  'filterData.dateLead': filterData.dateLead,
  'filterData.taskSelectedDate': filterData.taskSelectedDate
});

let dateLeadSD = startDate;
let dateLeadED = endDate;

if (filterData.dateLead && filterData.dateLead.value !== "custom") {
  const { startDate, endDate } =
    getDateRange(filterData.dateLead.value) || {};
  dateLeadSD = startDate;
  dateLeadED = endDate;
  logger.log('[applyFilter] Set dateLeadSD/ED from dateLead:', { dateLeadSD, dateLeadED });
}

logger.log('[applyFilter] Final dateLeadSD/ED before building filter:', { dateLeadSD, dateLeadED });

let taskTypes: string[] = [];

if (filterData?.taskType?.length) {
  filterData?.taskType?.map((e: any) => {
    taskTypes.push(e.value);
  });
}

let taskStartDate = taskFromDate,
  taskEndDate = taskToDate;

if (
  filterData?.taskSelectedDate &&
  filterData?.taskSelectedDate?.value !== "custom"
) {
  const { startDate, endDate } =
    getDateRange(filterData?.taskSelectedDate?.value) || {};
  taskStartDate = startDate;
  taskEndDate = endDate;
}

let assignLeadData: string[] = [];

if (filterData?.assignedLead && filterData?.assignedLead?.length) {
  filterData?.assignedLead?.map((e: any) => {
    assignLeadData.push(e.value);
  });
}

let activityTypes: string[] = [];

if (
  filterData?.customActivityName &&
  filterData?.customActivityName?.length
) {
  filterData?.customActivityName?.map((e: any) => {
    activityTypes.push(e.value);
  });
}

let activityStartDate = activityFromDate,
  activityEndDate = activityToDate;

if (
  filterData?.activitySelectedDate &&
  filterData?.activitySelectedDate?.value !== "custom"
) {
  const { startDate, endDate } =
    getDateRange(filterData?.activitySelectedDate?.value) || {};
  activityStartDate = startDate;
  activityEndDate = endDate;
}

let customLeadObj: any = {};

if (
  filterData?.selectCustomLead &&
  filterData?.operator &&
  (filterData?.leadformValue || filterData?.operator.value === "doesNotContain")
) {
  const selectedFieldType = customLeadOptions?.find(
    (e: any) => e.value === filterData?.selectCustomLead?.value
  )?.type;
  
  // For group fields, use the nested path from subfield selection
  // For regular fields, use the field value directly
  const fieldName = filterData?.selectCustomLeadSubField
    ? filterData.selectCustomLeadSubField.value  // e.g., "accessory.type"
    : filterData?.selectCustomLead?.value;       // e.g., "phone"

  customLeadObj.customFieldFilter = {
    fieldName: fieldName,
  };

  if (filterData?.operator.value === "between") {
    customLeadObj.customFieldFilter.range = {
      start: {
        operator: "gte",
        value: filterData?.leadformValue,
      },
      end: {
        operator: "lte",
        value: filterData?.endLeadformValue,
      },
    };
  } 
  // Special handling for multi-selection
  else if (selectedFieldType === "multi-selection" && filterData?.leadformValue) {
    // Convert comma-separated values to array if needed
    const valueArray = typeof filterData?.leadformValue === 'string' 
      ? filterData?.leadformValue.split(',').map((v: string) => v.trim())
      : filterData?.leadformValue;
      
    customLeadObj.customFieldFilter.single = {
      operator: filterData?.operator?.value,
      value: valueArray,
    };
  }
  else {
    customLeadObj.customFieldFilter.single = {
      operator: filterData?.operator?.value,
      value: filterData?.leadformValue,
    };
  }
}

let taskFilter: any = {};

if (filterData?.taskType?.length) {
  taskFilter = {
    types: taskTypes,
  };
}

if (filterData?.taskSelectedDate) {
  taskFilter = {
    ...taskFilter,
    range: {
      start: taskStartDate,
      end: taskEndDate,
    },
  };
}

// Create a local activityFilter object for building the activity filters
let activityFilter: any = {};

// If activity types are selected, add them to the filter
if (activityTypes?.length) {
  activityFilter.activityFilter = {
    types: activityTypes,
  };
}

// If activity date range is selected, add it to the filter
if (filterData?.activitySelectedDate) {
  activityFilter.activityFilter = {
    ...activityFilter?.activityFilter,
    range: {
      start: activityStartDate,
      end: activityEndDate,
    },
  };
}

// Add the new separate operators for duration and call count
if ((filterData.durationOperator && filterData.callDurationValue) || 
    (filterData.callCountOperator && filterData.callCountValue)) {
  
  // Initialize the nested structure if needed
  if (!activityFilter.activityFilter) {
    activityFilter.activityFilter = {};
  }
  
  // Set duration operator and value if provided
  if (filterData.durationOperator && filterData.callDurationValue) {
    activityFilter.activityFilter.durationOperator = filterData.durationOperator.value;
    activityFilter.activityFilter.duration = parseInt(filterData.callDurationValue, 10);
  }
  
  // Set call count operator and value if provided
  if (filterData.callCountOperator && filterData.callCountValue) {
    activityFilter.activityFilter.callCountOperator = filterData.callCountOperator.value;
    activityFilter.activityFilter.callCount = parseInt(filterData.callCountValue, 10);
  }
}

// ✅ FIXED: Explicit undefined for cleared filters (prevents sticky filter bug)
let obj: any = {
  // Sort - Keep existing logic (works correctly)
  ...(filterData.sortLeadBy
    ? {
        sort: {
          orderBy: filterData?.sortLeadBy?.value,
          isAscending: false,
        },
      }
    : {
        sort: {
          isAscending: false,
        },
      }),

  // Date - Keep existing logic (works correctly)
  ...(filterData.dateLead && dateLeadSD && dateLeadED && {
    date: {
      startedAt: dateLeadSD,
      endedAt: dateLeadED,
    },
  }),

  // ✅ FIXED: Basic filters - explicit undefined when cleared
  label: filterLabel.length ? filterLabel : undefined,
  status: filterStatus.length ? filterStatus : undefined,
  source: filterSource.length ? buildSourceFilter(filterData?.sources || []) : undefined,
  products: filterProducts.length ? filterProducts : undefined,

  // ✅ FIXED: Team filters - handle organization vs teams (mutually exclusive)
  byOrganization: isOrganization ? true : undefined,
  teams: !isOrganization && filterTeame.length ? filterTeame : undefined,

  // ✅ FIXED: Team members - handle both employee and assignedLead
  // When assignedLead is set, we need both isAssign flag AND teamMembers at top level
  teamMembers: filterEmp.length
    ? filterEmp
    : (filterData?.assignedLead?.length ? assignLeadData : undefined),

  // ✅ FIXED: isAssign flag at TOP LEVEL (not nested in assign object)
  isAssign: filterData?.assignedLead?.length ? true : undefined,

  // ✅ FIXED: Nested filters - explicit undefined when cleared
  taskFilter: Object.keys(taskFilter || {}).length ? taskFilter : undefined,
  activityFilter: Object.keys(activityFilter).length > 0 ? activityFilter.activityFilter : undefined,

  // ✅ FIXED: Custom field filter - extract from wrapper object
  customFieldFilter: customLeadObj?.customFieldFilter || undefined,
};

logger.log('Final filter object:', obj);
LeadFilterDataList(obj || {});

// Close the popup after applying filters
onClose();
setShowFilterDrawer(false);


};

// Prepare props for section components
const sectionProps: SectionProps = {
filterData,
handleFilter,
dateFilterOnChange,
advanceDateFilterOnChange,
startDate: setStartDate,
endDate: setEndDate,
showDatePicker,
datePickerKey,
setTaskFromDate,
setTaskToDate,
setActivityFromDate,
setActivityToDate,
labels,
status,
leadSource,
teamMembers,
employee,
customLeadOptions,
activityTypes,
taskOptions,
getOperatorOptions,
isOptionsExist,
isOrganization,
  products, 
  productsLoading,
};

// Map icon names to actual components
const getIconComponent = (iconName: string) => {
switch(iconName) {
  case 'Layers': return <Layers size={16} />;
  case 'Users': return <Users size={16} />;
  case 'Activity': return <Activity size={16} />;
  case 'Settings': return <Settings size={16} />;
   case 'Package': return <Package size={16} />; // ADD this
  default: return null;
}
};


logger.log('🔄 FILTER RENDER:', {
  timestamp: new Date().toISOString(),
  isOpen,
  propsChanged: Object.keys(props || {}),
  renderCount: ++renderCountRef.current // Add useRef for this
});

// The final JSX with sections rendered dynamically
return (
<PopupComponent
  isOpen={isOpen}
  onClose={onClose}
  title="Filter Leads"
  size="lg"
  footer={
    <div className="filter-popup-footer">
      <button
        type="button"
        className="btn btn-outline-secondary"
        onClick={resetFilter}
      >
        <RefreshCw size={14} className="me-1" />
        Reset
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={applyFilter}
      >
        Apply Filters
      </button>
    </div>
  }
  headerActions={
    <div className="filter-search-container">
      <div className="search-input-wrapper">
        <Search size={14} className="search-icon" />
        <input
          type="text"
          className="filter-search-input"
          placeholder="Search filters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="clear-search-btn"
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  }
>
  <div className="filter-popup-container">
    {/* Left column - filter categories */}
    <div className="filter-categories-column">
      {filteredCategories.map((category) => (
        <div
          key={category.id}
          className={`filter-category ${
            expandedSections[category.id] ? "expanded" : ""
          }`}
        >
          <div
            className="filter-category-header"
            onClick={() => toggleSection(category.id)}
          >
            <span className="category-icon">
              {getIconComponent(category.icon)}
            </span>
            <span className="category-label">{category.label}</span>
            <span className="expand-icon">
              {expandedSections[category.id] ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </span>
          </div>
        </div>
      ))}
    </div>

    {/* Right column - filter options */}
    <div className="filter-options-column">
      {filteredCategories.map((category) => (
        <div
          key={`options-${category.id}`}
          id={`filter-section-${category.id}`}
          className={`filter-options-section ${
            expandedSections[category.id] ? "expanded" : "collapsed"
          }`}
        >
          <div className="filter-options-header">
            <h3>{category.label} Options</h3>
          </div>

          <div className="filter-options-body">
            {/* Render the appropriate section based on category ID */}
            {category.id === "basic" && <BasicFiltersSection {...sectionProps} />}
            {category.id === "team" && <TeamFiltersSection {...sectionProps} />}
            {category.id === "activity" && <ActivityFiltersSection {...sectionProps} />}
            {category.id === "task" && <TaskFiltersSection {...sectionProps} />}
            {category.id === "products" && <ProductFiltersSection {...sectionProps} />}
          </div>
        </div>
      ))}
    </div>
  </div>
</PopupComponent>
);
};

export default LeadFilter;